import get from 'lodash/get';
import log from 'loglevel';
import moment from 'moment';
import convert from 'xml-js';

import { appReducerActions } from './appSlice';
import * as buyActions from './buy';
import * as devActions from './dev';
import * as dialogsSlice from './dialogs';
import * as clientActions from './client';
import * as wowsCommerceSlice from './wowsCommerce';

import type { Action } from 'redux';
import type { Thunk } from '~/store';
import type Api from '~/types';
import type { ShopMessage } from '~/typings/shop';

import { armoryOnAppInit } from '~/armory';
import ClientApi from '~/clientApi';
import { BlueprintViewerRef } from '~/Components/BlueprintViewer/BlueprintViewer';
import blueprintsState from '~/utils/blueprintsState';
import { getLength as getSvgPathLength } from '~/Components/BlueprintViewer/utils';
import {
    ACTION_IDS, BLUEPRINT_DURATION_MODIFIER, COLOR_ORDER, DWH_EXPORT_EVENTS, GRADES, SHOP_ACTIONS
} from '~/constants';
import api from '~/api';
import dwhExport from '~/dwhExport';
import { mapBoosters, mapRewards } from '~/mapping';
import preloaded from '~/preloaded';
import RefManager, { RefManagerKeys } from '~/refManager';
import { isOdd } from '~/utils';
import { getCurrentShipLevel, isGuidePassed } from '~/utils/account';
import {
    getBlueprintsCount, getLaunchingLevel, getVideoAction, isVideoWatched, mapActions
} from '~/utils/actions';
import animationIterator from '~/utils/animationIterator';
import { preloadBackgrounds } from '~/utils/preload';
import Sync from '~/utils/sync';
import { dropProgressSpeed } from '~/utils/updateProgressSpeed';
import { getStartBoosterProgressRestriction, getStartBoostersIds } from '~/utils/boosters';
import mapExtraLevels from '~/mapping/levels';


const SYNC_INTERVAL = 1000 * 10;
const SYNC_INTERVAL_PENDING = 1000;

export const HIDE_DIALOG_DELAY = 500;
export const EDUCATION_ACTION_ID = 'action_education';


// Reducer actions
export const {
    setAppIsReady,
    setBrowserIsReady,
    setTargetLevel,
    setProgressShipLevel,
    switchSync,
    showIntroVideo,
    showLaunchVideo,
    showCompleteBlueprintsVideo,
    showFinalVideo,
    setIssueRewardsPendingState,
    setGetDataPendingState,
    setWorldRendererState,
    enableBlur,
    disableBlur,
    setPreviousStep,
    setNextStep,
    setTimings,
    setPauseAnimation,
    updateBackButtonVisibility,
    setSetShipLevelPendingState,
    setBuyBoosterPendingState,
    setCompleteActionPendingState,
    setAnimationState,
    showRepeatSidebar,
    hideRepeatSidebar,
    setRepeatingState,
    updateAnimationLevel,
    updateAnimationSubtitle,
    clearBoosterId,
    setSelectedBooster,
    updateClientState,
    setShipLevel,
    setPredictProgress,
    setEventStartDate,
    setEventEndDate,
    setDefaultDates,
} = appReducerActions;


// Thunk actions
export const AppInit = (initFromArmory = false): Thunk => (dispatch, getState) => {
    dispatch(addMessageListeners());
    dispatch(devActions.addMockJsHost());
    dispatch(getData(preloaded.state.account, preloaded.state.config, initFromArmory));

    console.log('App init with account state:', preloaded.state.account);

    dispatch(clientActions.setClientStateListener());
    const state = getState();
    if (state.app.isInIframe) {
        armoryOnAppInit();
    }
};


export const makeCompensation = (): Thunk => (dispatch, getState) => {
    const state = getState();
    const eventum = get(state.app, 'account.wallet.eventum', 0) as number;

    if (eventum > 0) {
        dispatch(appReducerActions.switchSync(false));
        api.post('/accounts/change-extra-dockyardum/', {
            count: eventum,
        })
            .then((resp: Api.Account) => {
                dispatch(updateAccount(resp));
            })
            .catch((error: Error) => {
                dispatch(clientActions.showErrorMessage(error));
                log.error(error);
            })
            .finally(() => {
                dispatch(appReducerActions.switchSync(true));
            });
    }
};

export const issueRewards = (rewardIds?: Array<string>, fromDialog?: boolean): Thunk => (dispatch, getState) => {
    dispatch(appReducerActions.setIssueRewardsPendingState(true));
    const state = getState();

    if (state.dialogs.isWelcomeDialogOpen) {
        dispatch(dialogsSlice.closeWelcomeDialog());
    }
    dispatch(appReducerActions.switchSync(false));
    api.post('/accounts/issue-rewards/', {
        rewardIds: rewardIds || state.app.availableRewards,
    })
        .then((resp: Api.Account) => {
            if (state.dialogs.isWelcomeDialogOpen && !rewardIds) {
                dispatch(dialogsSlice.closeWelcomeDialog());
            }
            if (state.app.isEndReached && fromDialog) {
                dispatch(makeCompensation());
            }
            dispatch(updateAccount(resp));
        })
        .catch((error: Error) => {
            dispatch(clientActions.showErrorMessage(error));
            log.error(error);
        })
        .finally(() => {
            dispatch(appReducerActions.switchSync(true));
            dispatch(appReducerActions.setIssueRewardsPendingState(false));
        });
};


export const updateAvailableRewards = (collectShips = false): Thunk => (dispatch, getState) => {
    const availableRewardsIds: Array<string> = [];
    const autocollectRewardsIds: Array<string> = [];
    const state = getState();
    const progress = state.app.account?.progress;
    const issuedRewardsIds = state.app.account?.issuedRewardsIds;

    state.app.rewards.forEach((reward: Api.IReward) => {
        reward.gameRewards.forEach((gameReward) => {
            if (gameReward.type === 'ship' && reward.value <= Number(progress) && !issuedRewardsIds?.includes(reward.id)) {
                autocollectRewardsIds.push(reward.id);
            }
        });

        if (reward.value <= Number(progress) && !issuedRewardsIds?.includes(reward.id)) {
            availableRewardsIds.push(reward.id);
        }
    });

    if (autocollectRewardsIds.length > 0 && collectShips) {
        dispatch(issueRewards(autocollectRewardsIds));
    }

    dispatch(appReducerActions.updateAvailableRewards({
        availableRewardsIds,
    }));
};

export const updateAccount = (account: Api.Account, isInstant = false): Thunk => (dispatch, getState) => {
    if (account) {
        dropProgressSpeed();
        const state = getState();
        const currentShipLevel = getCurrentShipLevel(account, state.app.shipLevels);
        const lastLevel: number = state.app.shipLevels.length - 1;
        const isEndReached = lastLevel === currentShipLevel;
        const isEventFinished = moment.utc().unix() * 1000 >= state.app.progressFinishTime;

        const stateAccount: Api.IAccount = {
            ...account,
            issuedRewards: account.issuedRewardsIds?.map((id) => {
                return state.app.rewards.find((reward) => reward.id === id) as Api.IReward;
            }) || [],
            completedActions: account.completedActionsIds?.map((id) => {
                return state.app.actions.find((action) => action.name === id) as Api.Action;
            }) || [],
            wallet: {
                eventum: account.balance?.dockyardum || 0,
                gold: account.balance?.gold || 0,
            },
        };

        const startBoosterIds = getStartBoostersIds(state.app.boosters);
        const isStartBoostersBought = state.app?.account?.boughtBoostersIds && startBoosterIds.every((id) => {
            return state.app?.account?.boughtBoostersIds?.includes(id);
        });

        dispatch(appReducerActions.updateAccount({
            account: stateAccount,
            isEndReached,
            isEventFinished,
            lastLevel,
            isStartBoostersBought: !!isStartBoostersBought,
        }));
        if (!isEndReached) {
            dispatch(buyActions.buyBoosters(false));
        }
        dispatch(updateAvailableRewards());
        dispatch(dialogsSlice.showLastChanceScreen());
        if (account.progress !== state.app.progressShipLevel) {
            dispatch(appReducerActions.setTargetLevel({
                level: account.progress || 0,
            }));
            dispatch(appReducerActions.setProgressShipLevel({
                level: account.progress || 0,
            }));
        }
        dispatch(clientActions.updateClientState(account, isInstant));
    } else {
        log.error('Account:', account);
    }
};

export const checkAvailableRewards = (initFromArmory = false): Thunk => (dispatch, getState) => {
    const state = getState();
    const rewards = state.app.rewards.filter((reward) => {
        return state.app.availableRewards.includes(reward.id)
            && reward.gameRewards
            && reward.gameRewards.length > 0;
    });
    if (rewards.length > 0 && isGuidePassed(state.app.account.completedActionsIds)) {
        if (!initFromArmory) {
            dispatch(dialogsSlice.openWelcomeDialog(false));
        }
    }
};

export const appIsReady = (initFromArmory = false): Thunk => (dispatch) => {
    dispatch(appReducerActions.setAppIsReady());
    ClientApi.hideScreenshot();
    dispatch(checkAvailableRewards(initFromArmory));
};

export const checkGuideIsFinished = (): Thunk => (dispatch, getState) => {
    const state = getState();
    const isEducationPassed = isGuidePassed(state.app.account.completedActionsIds);

    if (!isEducationPassed && !preloaded.settings.skipTutorial) {
        dispatch(turnGuideOn());
    } else {
        dispatch(checkAvailableRewards());
    }
};


export const getData = (
    preloadedAccount?: Api.Account,
    preloadedConfig?: Api.Config,
    initFromArmory = false
): Thunk => (
    dispatch,
    getState,
) => {
    dispatch(dialogsSlice.hideLoadError());

    const setConfigData = (data: Api.ApiQuery) => {
        if (!data.config) {
            return;
        }

        const animationShipLevel = data.account
            ? getCurrentShipLevel(data.account, data.config?.shipLevels as Api.ShipLevel[])
            : 0;
        const actionLevels = mapActions(data.config?.actions as Api.Action[]);
        const spaId = data.account?.spaId as number;
        const rewards = mapRewards(data.config.rewards as Api.Reward[], data.config.shipLevels as Api.ShipLevel[]);
        const lastLevel = data.config.shipLevels?.length ? data.config.shipLevels?.length - 1 : 0;
        let freeLevels = lastLevel - (data.config.events?.levelsCompletedBySpendingDoubloons || 0);
        if (!freeLevels || freeLevels < 1) { freeLevels = 0; }
        const boosters = mapBoosters(data.config.boosters as Api.Booster[]);
        dispatch(appReducerActions.getData({
            showThirdCard: isOdd(spaId),
            mainRewardShipId: data.config.mainRewardShipId as string,
            mainRewardShipName: data.config.mainRewardName as string,
            secondaryShipLevels: mapExtraLevels(data.config.stuffLevels as Api.ExtraShipLevel[], false),
            tertiaryShipLevels: mapExtraLevels(data.config.furtherLevels as Api.ExtraShipLevel[], true),
            boosters,
            rewards,
            shipLevels: data.config?.shipLevels as Api.ShipLevel[],
            launchingLevelId: getLaunchingLevel(data),
            actions: data.config?.actions as Action[],
            actionLevels,
            isIntroWatched: data.account
                ? isVideoWatched(ACTION_IDS.INTRO_VIDEO, data.account.completedActionsIds)
                : false,
            progressFinishTime: Date.parse(
                data.config.events?.eventScheduleSettings?.progressFinishDatetime as string
            ),
            progressStartTime: Date.parse(
                data.config.events?.eventScheduleSettings?.openDatetime as string
            ),
            progressFinishTimeDefault: Date.parse(
                data.config.events?.eventScheduleSettings?.progressFinishDatetime as string
            ),
            progressStartTimeDefault: Date.parse(
                data.config.events?.eventScheduleSettings?.openDatetime as string
            ),
            currency: data.config.events?.currency as string,
            animationShipLevel,
            closeTime: Date.parse(data.config.events?.eventScheduleSettings?.closeDatetime as string),
            blueprintsCount: getBlueprintsCount(data.config.shipLevels as Api.ShipLevel[]),
            congratulationBackground: data.config.events?.eventContent?.arts?.congratulationBackground as string,
            shipPreviewBackground: data.config.events?.eventContent?.arts?.shipPreviewBackground as string,
            secondaryShipPreviewBackground:
                data.config.events?.eventContent?.arts?.secondaryShipPreviewBackground as string,
            educationBackground: data.config.events?.eventContent?.arts?.educationBackground as string,
            kitBackgrounds: data.config.events?.eventContent?.arts?.kitBackgrounds as string[],
            blueprintsBackgrounds:
                data.config?.events?.eventContent?.arts?.blueprintsBackgrounds as Api.BlueprintsBackground[],
            blueprintArts: [],
            plugOnWeb: data.config.events?.eventContent?.arts?.plugOnWeb as string,
            purchaseConfirmationBackground:
                data.config.events?.eventContent?.arts?.purchaseConfirmationBackground as string,
            banner: data.config.events?.eventContent?.banner || null,
            readMoreLink: data.config.events?.eventContent?.readMoreLink as unknown as string,
            rules: data.config.events?.eventContent?.rules as Api.Rule[],
            compensations: data.config.events?.eventContent?.rewardForExchange?.items as CompensationItem[],
            specialRewards: rewards.filter((reward) => reward.grade !== GRADES.NORMAL),
            educationSteps: data.config.events?.eventContent?.education as Api.EducationStep[],
            levelsCompletedBySpendingDoubloons: data.config.events?.levelsCompletedBySpendingDoubloons as number,
            freeLevels,
            lastLevel,
            starterPacksMaxLevel: getStartBoosterProgressRestriction(boosters),
            timings: data.config.events?.eventContent?.progressbarDurations as number[],
            missionsFinishVersion: data.config.events?.finishSseVersion?.[0] as string,
            eventFinishVersion: data.config.events?.finishSseVersion?.[1] as string,
            showDevPanel: data.config.events?.eventMode?.showDevPanel || false,
            showResetButton: data.config.events?.eventMode?.showResetButton || false,
        }));

        dispatch(updateAccount(data.account as Api.IAccount, true));
        dispatch(clientActions.updateAnimationSubtitle(animationShipLevel, true));
        const state = getState();

        // preload(state)
        preloadBackgrounds(state);

        if (state.app.isIntroWatched || initFromArmory) {
            dispatch(appReducerActions.setGetDataPendingState(false));
        } else {
            dispatch(dialogsSlice.showIntroVideo());
            dispatch(turnGuideOn());
        }

        dispatch(appIsReady(initFromArmory));

        dwhExport.send(DWH_EXPORT_EVENTS.APP_LOADED);

        if (data.account) {
            // eslint-disable-next-line no-new
            new Sync(SYNC_INTERVAL, false, dispatch, getState); // regularSync
            // eslint-disable-next-line no-new
            new Sync(SYNC_INTERVAL_PENDING, true, dispatch, getState); // pendingSync
        }
    };

    if (
        preloadedAccount
        && preloadedConfig
        && Object.keys(preloadedAccount).length > 0
        && Object.keys(preloadedConfig).length > 0
    ) {
        if (getState().app.isInGame) {
            dispatch(appReducerActions.setGetDataPendingState(true));
        }
        const account: Api.Account = preloadedAccount as Api.Account;
        const config: Api.Config = preloadedConfig as Api.Config;
        const data: Api.ApiQuery = {
            account,
            config,
        };
        setConfigData(data);
        if (!data.account?.isActivated && !ClientApi.isInGame()) {
            dispatch(dialogsSlice.showDownloadGamePopup());
        } else {
            dispatch(dialogsSlice.hideDownloadGamePopup());
        }
    } else {
        dispatch(appReducerActions.setGetDataPendingState(true));
        const getDataQueryArr = [api.get('/config/current/'), api.get('/accounts/account-info/')];

        Promise.all(getDataQueryArr)
            .then((responses) => {
                let data: Api.ApiQuery = {};
                data = {
                    config: responses[0],
                    account: responses[1],
                };
                setConfigData(data);
                dispatch(appReducerActions.setGetDataPendingState(false));
            })
            .catch((errors: Array<Error>) => {
                dispatch(appReducerActions.setGetDataPendingState(false));
                if (Array.isArray(errors)) {
                    errors.forEach((error: Error) => {
                        dispatch(clientActions.showErrorMessage(error));
                        log.error(error);
                    });
                } else {
                    dispatch(clientActions.showErrorMessage(errors));
                    log.error(errors);
                }
                dispatch(dialogsSlice.showLoadError());
            });
    }
};

export const turnGuideOn = (): Thunk => (dispatch) => {
    dispatch(appReducerActions.turnGuideOn());
};

export const turnGuideOff = (): Thunk => (dispatch) => {
    dispatch(appReducerActions.turnGuideOff());
    dispatch(checkAvailableRewards());
};

export const addEventin = (value: number): Thunk => (dispatch, getState) => {
    const { currency } = getState().app;
    api.post('/accounts/replenish-wallet/', {
        price: {
            currency,
            amount: value,
        },
    })
        .then((resp: Api.Account) => {
            dispatch(updateAccount(resp));
        })
        .catch((error: Error) => {
            dispatch(clientActions.showErrorMessage(error));
            log.error(error);
        });
};

export const deleteAccount = (): Thunk => (dispatch, getState) => {
    const state = getState();
    const spaId: number = Number(state.app.account.spaId) || 0;

    api.delete(`/accounts/${spaId}`)
        .then(() => {
            log.log('Account deleted');
            dispatch(clientActions.updateAnimationSubtitle(0, true));
            dispatch(sync());
        })
        .catch((error: Error) => {
            dispatch(clientActions.showErrorMessage(error));
            log.error(error);
        });
};

export const stopRepeatBuildingProcess = (clb?: () => void, stop?: number): Thunk => (dispatch, getState) => {
    ClientApi.clearWaitingList();

    const blueprintsViewer = RefManager.getRef(RefManagerKeys.BlueprintViewer) as BlueprintViewerRef;
    if (blueprintsViewer) {
        blueprintsViewer.setIsInstant(true);
    }
    const {
        account, animationShipLevel, lastLevel, nextStep, previousStep, blueprintsCount
    } = getState().app;
    const currentShipLevel = stop || animationShipLevel;

    animationIterator.forceStop();

    if (currentShipLevel > blueprintsCount) {
        dispatch(dialogsSlice.hideBlueprintsViewer());
    }

    dispatch(appReducerActions.setAnimationState(false));
    dispatch(appReducerActions.setRepeatingState(false));
    dispatch(appReducerActions.switchSync(true));

    const afterSetShipLevelLocal = (level: number) => {
        dispatch(
            clientActions.updateAnimationSubtitle(level, true, () => {
                if (blueprintsViewer) {
                    blueprintsViewer.setIsInstant(false);
                }
                if (level === lastLevel) {
                    const { completedActionsIds } = account;
                    const isFinalVideoSeen = completedActionsIds?.includes(ACTION_IDS.FINAL_VIDEO);
                    if (!isFinalVideoSeen) {
                        dispatch(dialogsSlice.showFinalVideo());
                    }
                }
                if (clb) {
                    clb();
                }
            }),
        );
    };

    const setLevel = (level: number) => {
        dispatch(
            clientActions.setShipLevelLocal(level, true, () => {
                dropProgressSpeed(true);
                afterSetShipLevelLocal(level);
            }),
        );
    };
    if (nextStep) {
        dispatch(appReducerActions.setNextStep(false));
        dispatch(
            clientActions.setShipLevelLocal(currentShipLevel + 1, true, () => {
                setLevel(currentShipLevel);
            }),
        );
    } else if (previousStep) {
        dispatch(appReducerActions.setPreviousStep(false));
        const level = currentShipLevel === 0 ? currentShipLevel : currentShipLevel - 1;
        setLevel(level);
    } else {
        setLevel(currentShipLevel);
        dispatch(appReducerActions.setTargetLevel({ level: currentShipLevel }));
    }
};

export const pauseRepeatBuildingProcess = (): Thunk => (dispatch, getState) => {
    ClientApi.clearWaitingList();
    const state = getState();

    animationIterator.forceStop();

    dispatch(appReducerActions.setTargetLevel({
        level: state.app.animationShipLevel,
    }));

    dispatch(appReducerActions.setAnimationState(false));
    dispatch(appReducerActions.setRepeatingState(false));
    dispatch(appReducerActions.switchSync(true));
};

export const repeatBuildingProcess = (fromLevel = 0): Thunk => (dispatch, getState) => {
    ClientApi.clearWaitingList();
    blueprintsState.clear();
    const blueprintsViewer = RefManager.getRef(RefManagerKeys.BlueprintViewer) as BlueprintViewerRef;
    const { account, isRepeating, blueprintsCount } = getState().app;

    const repeat = () => {
        const currentShipLevel = account.progress;
        const level = fromLevel === 0 ? fromLevel : fromLevel - 1;

        if (blueprintsViewer) {
            blueprintsViewer.setIsInstant(true);
        }

        dispatch(appReducerActions.setRepeatingState(true));
        dispatch(appReducerActions.switchSync(false));
        dispatch(
            clientActions.setShipLevelLocal(level, true, () => {
                dispatch(
                    clientActions.updateAnimationSubtitle(level, true, () => {
                        if (level < blueprintsCount) {
                            dispatch(dialogsSlice.showBlueprintsViewer());
                        }
                        setTimeout(() => {
                            if (blueprintsViewer) {
                                blueprintsViewer.setIsInstant(false);
                            }
                            dispatch(clientActions.setShipLevelLocal(currentShipLevel as number, false));
                        }, 100);
                    }),
                );
            }),
        );
    };

    if (isRepeating) {
        dispatch(stopRepeatBuildingProcess(repeat));
    } else {
        repeat();
    }
};

export const afterSkipVideo = (videoType: VideoType): Thunk => (dispatch, getState) => {
    const state = getState();
    const videoAction = getVideoAction(state.app.actions, videoType);
    const { completedActionsIds } = state.app.account;
    const isFinalPopupSeen = completedActionsIds?.includes(ACTION_IDS.GOLD_REWARD_POPUP);

    dispatch(completeAction(videoAction.name as string));
    if (videoType === 'intro') {
        dispatch(checkGuideIsFinished());
        dispatch(appIsReady());
    }

    if (videoType === 'launch') {
        animationIterator.continueAction();
    }

    if (videoType === 'final') {
        if (isFinalPopupSeen) {
            animationIterator.actionFinished();
        } else {
            dispatch(dialogsSlice.openFinalDialog(state.app.mainRewardShipName));
        }
    }

    if (videoType === 'completeBlueprints') {
        animationIterator.continueAction();
    }
};

export const completeAction = (actionId: string): Thunk => (dispatch) => {
    dispatch(appReducerActions.setCompleteActionPendingState(true));
    dispatch(appReducerActions.switchSync(false));
    api.post('/accounts/complete-action/', {
        actionId,
    })
        .then((resp: Api.Account) => {
            dispatch(updateAccount(resp));
        })
        .catch((error: Error) => {
            dispatch(clientActions.showErrorMessage(error));
            log.error(error);
        })
        .finally(() => {
            dispatch(appReducerActions.switchSync(true));
            dispatch(appReducerActions.setCompleteActionPendingState(false));
        });
};

export const sync = (): Thunk => (dispatch) => {
    api.get('/accounts/account-info/')
        .then((resp: Api.Account) => {
            dispatch(updateAccount(resp));
        })
        .catch((error: Error) => {
            dispatch(clientActions.showErrorMessage(error));
        });
};

export const addMessageListeners = (): Thunk => (dispatch) => {
    const onMessage = (e: MessageEvent<ShopMessage>) => {
        const msg: ShopMessage = e.data;
        switch (msg.action) {
            case SHOP_ACTIONS.ON_ROUTE_CHANGE: {
                dispatch(appReducerActions.updateBackButtonVisibility(msg.payload.path));
                break;
            }
            case SHOP_ACTIONS.LOADER_STATE: {
                dispatch(wowsCommerceSlice.setShopLoadingState(msg.payload.visibility));
                break;
            }
            case SHOP_ACTIONS.WINSTORE_INIT: {
                dispatch(setWinStoreCallbacks(msg.payload.callbacks));
                break;
            }
            default: {
                break;
            }
        }
    };

    window.addEventListener('message', onMessage);
};

export const setWinStoreCallbacks = (methods: Array<string>): Thunk => () => {
    const send = (method: string, args: unknown[]) => {
        const frame = RefManager.getRef(RefManagerKeys.SteambotFrame) as HTMLIFrameElement;
        if (frame) {
            const msg = {
                action: 'ON_WINSTORE_CALLBACK',
                payload: {
                    name: method,
                    args,
                },
            };
            frame.contentWindow?.postMessage(msg, '*');
        }
    };

    window.winStore = {};

    methods.forEach((method) => {
        window.winStore[method] = (...rest: unknown[]) => {
            send(method, rest);
        };
    });
};

export const getBlueprintsSvgData = (): Thunk => (dispatch, getState) => {
    const getPrevColor = (color: LINE_COLORS): LINE_COLORS | null => {
        const curentColorIndex = COLOR_ORDER[color];
        if (curentColorIndex === 0) {
            return null;
        }
        let c: LINE_COLORS | null = null;
        const prevColorIndex = curentColorIndex - 1;
        for (const key in COLOR_ORDER) {
            if (COLOR_ORDER.hasOwnProperty(key)) {
                const selector = key as LINE_COLORS;
                if (COLOR_ORDER[selector] === prevColorIndex) {
                    c = selector;
                }
            }
        }
        return c;
    };

    const svgData: BlueprintsSvgData = {};
    const state = getState();
    const durations: Array<number> = [];

    const loadLevelSvg = (level: number, maxLevel: number) => {
        const shipLevel = state.app.shipLevels[level];
        const url = shipLevel.blueprintsSvg;

        fetch(url as string)
            .then((response) => response.text())
            .then((svg) => {
                const svgJs = convert.xml2js(svg);
                const rootElement = get(svgJs, 'elements[0]', undefined) as { elements: SvgElement[] };
                if (rootElement && rootElement.elements) {
                    const levelDurations: Array<number> = [];
                    const groupDurations: GroupDurations = {};

                    const sortedElements = rootElement.elements.sort((a: SvgElement, b: SvgElement) => {
                        const aColor: LINE_COLORS = a.attributes.stroke || a.attributes.fill;
                        const bColor: LINE_COLORS = b.attributes.stroke || b.attributes.fill;
                        return COLOR_ORDER[aColor] - COLOR_ORDER[bColor];
                    });

                    const pathDataArray = sortedElements.map((element: SvgElement) => {
                        const color = element.attributes.stroke || element.attributes.fill;
                        if (!groupDurations[color]) {
                            groupDurations[color] = [];
                        }
                        const path = element.attributes.d;
                        const length = getSvgPathLength(path);
                        let duration = length / BLUEPRINT_DURATION_MODIFIER;
                        if (duration > 5) {
                            duration = 5;
                        }
                        if (duration < 1) {
                            duration = 1;
                        }

                        groupDurations[color].push(duration);

                        const getDelay = (color: LINE_COLORS) => {
                            const currentIndex = COLOR_ORDER[color];
                            const prevColor = getPrevColor(color);
                            if (prevColor) {
                                let delay = 0;
                                for (const key in COLOR_ORDER) {
                                    if (COLOR_ORDER.hasOwnProperty(key)) {
                                        const selector = key as LINE_COLORS;
                                        const i = COLOR_ORDER[selector];
                                        if (i < currentIndex) {
                                            const colorTimings = groupDurations[selector];
                                            delay += Math.max(...colorTimings);
                                        }
                                    }
                                }
                                return delay;
                            }
                            return 0;
                        };

                        const delay = getDelay(color);
                        levelDurations.push(duration + delay);

                        return {
                            path,
                            delay,
                            duration,
                        };
                    });

                    const longestLevelDuration = Math.max(...levelDurations);
                    durations.push(Math.round(longestLevelDuration));

                    svgData[level] = pathDataArray;

                    if (level < maxLevel) {
                        loadLevelSvg(level + 1, maxLevel);
                    }
                    if (level === maxLevel) {
                        const newTimings = [...state.app.timings];
                        durations.forEach((duration, index) => {
                            newTimings[index] = duration;
                        });
                        dispatch(appReducerActions.setTimings(newTimings));

                        dispatch(appReducerActions.getBlueprintsSvgData({
                            data: svgData,
                            durations,
                        }));
                    }
                }
            })
            .catch((error: unknown) => {
                console.error(error);
            });
    };

    loadLevelSvg(0, state.app.blueprintsCount);
};
